import React, { useEffect, useState } from "react";
import { Box, Button, Chip, Flex, Heading, Notification, Table, Text, TextField } from "@telia/teddy";
import { API_URL_SUPERADMIN_NEWS, API_URL_SUPERADMIN_NEWS_ITEM } from "../../api";

type NewsType = "INFO" | "WARNING" | "ERROR";
type Chain = "ELKJOP" | "POWER" | "D2D" | "TM_INTERNAL" | "TM_EXTERNAL";

type NewsItem = {
    id: string;
    heading: string;
    ingress: string;
    link: string | null;
    newsType: NewsType;
    chain: Chain | null;
    showOnImportantPages: boolean;
    displayFrom: string | null;
    displayTo: string | null;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
};

type FormState = {
    heading: string;
    ingress: string;
    link: string;
    newsType: NewsType | "";
    chain: Chain | "";
    showOnImportantPages: boolean;
    displayFrom: string;
    displayTo: string;
};

const emptyForm: FormState = {
    heading: "",
    ingress: "",
    link: "",
    newsType: "",
    chain: "",
    showOnImportantPages: false,
    displayFrom: "",
    displayTo: "",
};

const NEWS_TYPE_LABELS: Record<NewsType, string> = {
    INFO: "Info",
    WARNING: "Advarsel",
    ERROR: "Feil",
};

const CHAIN_LABELS: Record<Chain, string> = {
    ELKJOP: "Elkjøp",
    POWER: "Power",
    D2D: "D2D",
    TM_INTERNAL: "TM intern",
    TM_EXTERNAL: "TM ekstern",
};

const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("nb-NO", { day: "2-digit", month: "2-digit", year: "numeric" });

const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString("nb-NO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

// Convert ISO string from DB to datetime-local input value ("YYYY-MM-DDTHH:mm")
const toDateTimeLocal = (iso: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const NewsAdmin: React.FC = () => {
    const [news, setNews] = useState<NewsItem[]>([]);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const fetchNews = () => {
        setLoading(true);
        fetch(API_URL_SUPERADMIN_NEWS)
            .then((res) => {
                if (!res.ok) throw new Error(`Serverfeil: ${res.status}`);
                return res.json() as Promise<NewsItem[]>;
            })
            .then(setNews)
            .catch((err: Error) => setError(err.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchNews();
    }, []);

    const resetForm = () => {
        setForm(emptyForm);
        setEditingId(null);
        setError(null);
    };

    const handleEdit = (item: NewsItem) => {
        setEditingId(item.id);
        setForm({
            heading: item.heading,
            ingress: item.ingress,
            link: item.link ?? "",
            newsType: item.newsType,
            chain: item.chain ?? "",
            showOnImportantPages: item.showOnImportantPages,
            displayFrom: toDateTimeLocal(item.displayFrom),
            displayTo: toDateTimeLocal(item.displayTo),
        });
        setError(null);
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm("Er du sikker på at du vil slette denne nyheten?")) return;
        try {
            const res = await fetch(API_URL_SUPERADMIN_NEWS_ITEM(id), { method: "DELETE" });
            if (!res.ok) throw new Error(`Serverfeil: ${res.status}`);
            fetchNews();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Ukjent feil");
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!form.heading.trim()) return setError("Overskrift er påkrevd");
        if (!form.ingress.trim()) return setError("Ingress er påkrevd");
        if (!form.newsType) return setError("Nyhetstype er påkrevd");

        const body = {
            heading: form.heading.trim(),
            ingress: form.ingress.trim(),
            link: form.link.trim() || undefined,
            newsType: form.newsType,
            chain: form.chain,
            showOnImportantPages: form.showOnImportantPages,
            displayFrom: form.displayFrom ? new Date(form.displayFrom).toISOString() : null,
            displayTo: form.displayTo ? new Date(form.displayTo).toISOString() : null,
        };

        try {
            const res = await fetch(editingId ? API_URL_SUPERADMIN_NEWS_ITEM(editingId) : API_URL_SUPERADMIN_NEWS, {
                method: editingId ? "PUT" : "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const data = (await res.json()) as { error?: string };
                throw new Error(data.error ?? `Serverfeil: ${res.status}`);
            }
            resetForm();
            fetchNews();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Ukjent feil");
        }
    };

    return (
        <div className="retailer-dashboard-wrapper">
            <Heading as="h1" variant="title-300" mb="400">
                Nyheter
            </Heading>

            {error && (
                <Notification type="tile" variant="error" mb="300">
                    <Notification.Icon />
                    <Notification.Text>{error}</Notification.Text>
                </Notification>
            )}

            <Box mb="600">
                <Heading as="h2" variant="title-200" mb="300">
                    {editingId ? "Rediger nyhet" : "Ny nyhet"}
                </Heading>
                <form onSubmit={handleSubmit}>
                    <Flex direction="column" gap="300">
                        <TextField>
                            <TextField.Label>Overskrift</TextField.Label>
                            <TextField.Input
                                value={form.heading}
                                onChange={(e) => setForm((f) => ({ ...f, heading: e.target.value }))}
                            />
                        </TextField>

                        <TextField>
                            <TextField.Label>Beskrivelse</TextField.Label>
                            <TextField.Textarea
                                value={form.ingress}
                                onChange={(e) => setForm((f) => ({ ...f, ingress: e.target.value }))}
                                rows={4}
                            />
                        </TextField>

                        <TextField>
                            <TextField.Label>Lenke (valgfri)</TextField.Label>
                            <TextField.Input
                                value={form.link}
                                onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))}
                                placeholder="https://..."
                            />
                        </TextField>

                        <Box>
                            <Text mb="100">Type</Text>
                            <Chip
                                type="single"
                                value={form.newsType}
                                onValueChange={(val) => val && setForm((f) => ({ ...f, newsType: val as NewsType }))}
                                variant="filter"
                            >
                                {(["INFO", "WARNING", "ERROR"] as NewsType[]).map((t) => (
                                    <Chip.Item key={t} value={t}>
                                        {NEWS_TYPE_LABELS[t]}
                                    </Chip.Item>
                                ))}
                            </Chip>
                        </Box>

                        <Box>
                            <Text mb="100">Kjede</Text>
                            <Chip
                                type="single"
                                value={form.chain === "" ? "ALL" : form.chain}
                                onValueChange={(val) =>
                                    val && setForm((f) => ({ ...f, chain: (val === "ALL" ? "" : val) as Chain | "" }))
                                }
                                variant="filter"
                            >
                                <Chip.Item value="ALL">Alle</Chip.Item>
                                <Chip.Item value="ELKJOP">Elkjøp</Chip.Item>
                                <Chip.Item value="POWER">Power</Chip.Item>
                                <Chip.Item value="D2D">D2D</Chip.Item>
                                <Chip.Item value="TM_INTERNAL">TM intern</Chip.Item>
                                <Chip.Item value="TM_EXTERNAL">TM ekstern</Chip.Item>
                            </Chip>
                        </Box>

                        <Box>
                            <Text mb="100">Vis på viktige sider</Text>
                            <Chip
                                type="single"
                                value={form.showOnImportantPages ? "true" : "false"}
                                onValueChange={(val) =>
                                    val &&
                                    setForm((f) => ({
                                        ...f,
                                        showOnImportantPages: val === "true",
                                        ...(val === "false" && { displayFrom: "", displayTo: "" }),
                                    }))
                                }
                                variant="filter"
                            >
                                <Chip.Item value="false">Nei</Chip.Item>
                                <Chip.Item value="true">Ja</Chip.Item>
                            </Chip>
                        </Box>

                        {form.showOnImportantPages && (
                            <>
                                <TextField>
                                    <TextField.Label>Vis fra (valgfri)</TextField.Label>
                                    <TextField.Input
                                        type="datetime-local"
                                        value={form.displayFrom}
                                        onChange={(e) => setForm((f) => ({ ...f, displayFrom: e.target.value }))}
                                    />
                                </TextField>

                                <TextField>
                                    <TextField.Label>Vis til (valgfri)</TextField.Label>
                                    <TextField.Input
                                        type="datetime-local"
                                        value={form.displayTo}
                                        onChange={(e) => setForm((f) => ({ ...f, displayTo: e.target.value }))}
                                    />
                                </TextField>
                            </>
                        )}

                        <Flex gap="200">
                            <Button type="submit" variant="primary">
                                {editingId ? "Lagre endringer" : "Opprett nyhet"}
                            </Button>
                            {editingId && (
                                <Button type="button" variant="secondary" onClick={resetForm}>
                                    Avbryt
                                </Button>
                            )}
                        </Flex>
                    </Flex>
                </form>
            </Box>

            {loading ? (
                <Text>Laster...</Text>
            ) : news.length === 0 ? (
                <Text>Ingen nyheter opprettet ennå.</Text>
            ) : (
                <Table>
                    <Table.Header>
                        <Table.Row>
                            <Table.Head>Overskrift</Table.Head>
                            <Table.Head>Type</Table.Head>
                            <Table.Head>Kjede</Table.Head>
                            <Table.Head>Viktige sider</Table.Head>
                            <Table.Head>Visningsperiode</Table.Head>
                            <Table.Head>Opprettet av</Table.Head>
                            <Table.Head>Sist endret</Table.Head>
                            <Table.Head />
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {news.map((item) => (
                            <Table.Row key={item.id}>
                                <Table.Cell>
                                    <Text>{item.heading}</Text>
                                    <Text variant="additional-100">{item.ingress}</Text>
                                    {item.link && (
                                        <Text variant="additional-100">
                                            <a href={item.link} target="_blank" rel="noopener noreferrer">
                                                {item.link}
                                            </a>
                                        </Text>
                                    )}
                                </Table.Cell>
                                <Table.Cell>{NEWS_TYPE_LABELS[item.newsType]}</Table.Cell>
                                <Table.Cell>{item.chain ? CHAIN_LABELS[item.chain] : "Alle"}</Table.Cell>
                                <Table.Cell>{item.showOnImportantPages ? "Ja" : "Nei"}</Table.Cell>
                                <Table.Cell style={{ whiteSpace: "nowrap" }}>
                                    {item.displayFrom || item.displayTo ? (
                                        <>
                                            {item.displayFrom ? formatDateTime(item.displayFrom) : "–"}
                                            {" → "}
                                            {item.displayTo ? formatDateTime(item.displayTo) : "–"}
                                        </>
                                    ) : (
                                        "Alltid"
                                    )}
                                </Table.Cell>
                                <Table.Cell>{item.createdBy}</Table.Cell>
                                <Table.Cell style={{ whiteSpace: "nowrap" }}>{formatDate(item.updatedAt)}</Table.Cell>
                                <Table.Cell>
                                    <Flex gap="200">
                                        <Button variant="secondary" size="sm" onClick={() => handleEdit(item)}>
                                            Rediger
                                        </Button>
                                        <Button variant="secondary" size="sm" onClick={() => handleDelete(item.id)}>
                                            Slett
                                        </Button>
                                    </Flex>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            )}
        </div>
    );
};

export default NewsAdmin;
